import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingBag, Plus, RefreshCw, Trash2, CheckCircle, XCircle, Clock, AlertCircle, AlertTriangle, ExternalLink, Calendar as CalendarIcon, Webhook, Radio, Bell, Package, Users, DollarSign, RotateCcw, MessageCircle, ShoppingCart, KeyRound, Truck, Barcode, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import ChannelDisplayNameField from '../components/ChannelDisplayNameField';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDateTime, cn } from '@/lib/utils';
import { format, parseISO } from 'date-fns';
import SuperAdminCompanySelector, { useSuperAdminCompany } from '@/components/SuperAdminCompanySelector';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// All available Shopify webhook topics with descriptions
const WEBHOOK_TOPICS = [
  { topic: 'orders/create', label: 'Order Created', description: 'When a new order is placed', category: 'Orders', icon: ShoppingBag },
  { topic: 'orders/updated', label: 'Order Updated', description: 'When an order is modified', category: 'Orders', icon: RefreshCw },
  { topic: 'orders/paid', label: 'Order Paid', description: 'When payment is received', category: 'Orders', icon: DollarSign },
  { topic: 'orders/cancelled', label: 'Order Cancelled', description: 'When an order is cancelled', category: 'Orders', icon: XCircle },
  { topic: 'orders/edited', label: 'Order Edited', description: 'When items are added/removed', category: 'Orders', icon: Package },
  { topic: 'orders/fulfilled', label: 'Order Fulfilled', description: 'When order is fully shipped', category: 'Orders', icon: CheckCircle },
  { topic: 'orders/partially_fulfilled', label: 'Order Partially Fulfilled', description: 'When order is partially shipped', category: 'Orders', icon: Clock },
  { topic: 'refunds/create', label: 'Refund Created', description: 'When a refund is issued', category: 'Orders', icon: RotateCcw },
  { topic: 'checkouts/create', label: 'Checkout Created', description: 'When a checkout is started (abandoned cart)', category: 'Checkouts', icon: ShoppingCart },
  { topic: 'checkouts/update', label: 'Checkout Updated', description: 'When a checkout is updated', category: 'Checkouts', icon: RefreshCw },
  { topic: 'products/create', label: 'Product Created', description: 'When a new product is added', category: 'Products', icon: Package },
  { topic: 'products/update', label: 'Product Updated', description: 'When a product is modified', category: 'Products', icon: RefreshCw },
  { topic: 'products/delete', label: 'Product Deleted', description: 'When a product is removed', category: 'Products', icon: Trash2 },
  { topic: 'customers/create', label: 'Customer Created', description: 'When a new customer registers', category: 'Customers', icon: Users },
  { topic: 'customers/update', label: 'Customer Updated', description: 'When customer info changes', category: 'Customers', icon: RefreshCw },
  { topic: 'inventory_levels/update', label: 'Inventory Updated', description: 'When stock levels change', category: 'Inventory', icon: Package },
];

const ShopifyIntegrationPage = () => {
  const [stores, setStores] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [shopDomain, setShopDomain] = useState('');
  const [customApiKey, setCustomApiKey] = useState('');
  const [customClientSecret, setCustomClientSecret] = useState('');
  const [showCustomCredentials, setShowCustomCredentials] = useState(false);
  const [orderSyncDialogOpen, setOrderSyncDialogOpen] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [syncSinceDate, setSyncSinceDate] = useState('');
  
  // Sync settings state per store
  const [syncSettings, setSyncSettings] = useState({});
  const [savingSyncSettings, setSavingSyncSettings] = useState({});
  
  // Super Admin company selector
  const { 
    selectedCompanyId, 
    setSelectedCompanyId, 
    needsCompanySelector, 
    isReady 
  } = useSuperAdminCompany();
  
  // Webhook states
  const [webhooks, setWebhooks] = useState([]);
  const [webhookHealth, setWebhookHealth] = useState({});
  const [webhooksLoading, setWebhooksLoading] = useState(false);
  const [registeringWebhooks, setRegisteringWebhooks] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState([]);

  // Multi-origin routing (per-tenant): when true, on every Shopify
  // orders/create webhook where every line item has origin_location=null
  // we fetch the FulfillmentOrder API to learn the assigned location
  // per line. Default OFF so single-warehouse tenants don't pay the
  // extra ~200 ms latency per webhook.
  const [multiOriginEnabled, setMultiOriginEnabled] = useState(false);
  const [savingMultiOrigin, setSavingMultiOrigin] = useState(false);

  const loadMultiOriginSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(
        `${API_BASE_URL}/shopify/settings/multi-origin-routing`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMultiOriginEnabled(!!r.data?.enabled);
    } catch (_e) {
      // Endpoint may be 404 on older deployments — leave at default.
    }
  };

  const toggleMultiOrigin = async (next) => {
    setSavingMultiOrigin(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.put(
        `${API_BASE_URL}/shopify/settings/multi-origin-routing`,
        { enabled: !!next },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMultiOriginEnabled(!!r.data?.enabled);
      toast.success(
        next
          ? 'Multi-origin routing enabled — Opswarex will now fetch FulfillmentOrder data for web orders.'
          : 'Multi-origin routing disabled — orders will use FIFO when origin_location is null.'
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update setting.');
    } finally {
      setSavingMultiOrigin(false);
    }
  };

  useEffect(() => {
    if (isReady) {
      loadStores();
      loadSyncHistory();
      loadMultiOriginSetting();
    } else {
      setLoading(false);
    }
  }, [isReady, selectedCompanyId]);

  // Load webhooks when stores are loaded
  useEffect(() => {
    if (stores.length > 0) {
      loadWebhooks();
      loadWebhookHealth();
    }
  }, [stores]);

  const [skuAudit, setSkuAudit] = useState(null);
  const [skuAuditLoading, setSkuAuditLoading] = useState(false);

  const runSkuAudit = async (apply) => {
    setSkuAuditLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE_URL}/shopify/sku-audit`, { apply }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSkuAudit(res.data);
      if (apply) {
        toast.success(`${res.data.updated} SKU${res.data.updated === 1 ? '' : 's'} fixed on Shopify`);
        if (res.data.errors?.length) toast.warning(`${res.data.errors.length} could not be updated`);
      } else if (res.data.mismatches.length === 0) {
        toast.success('All linked Shopify SKUs match your inventory');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SKU audit failed');
    } finally {
      setSkuAuditLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/shopify/stores`;
      if (needsCompanySelector && selectedCompanyId) {
        url += `?company_id=${selectedCompanyId}`;
      }
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(response.data.stores);
    } catch (error) {
      toast.error('Failed to load Shopify stores');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/shopify/sync/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSyncHistory(response.data.history);
    } catch (error) {
      console.error('Failed to load sync history');
    }
  };

  const loadWebhooks = async () => {
    setWebhooksLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/shopify/webhooks/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Failed to load webhooks:', error);
      // Don't show error toast for webhooks - might be no store connected
    } finally {
      setWebhooksLoading(false);
    }
  };

  const loadWebhookHealth = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/shopify/webhooks/health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const map = {};
      (response.data.stores || []).forEach((h) => { map[h.store_id] = h; });
      setWebhookHealth(map);
    } catch (error) {
      console.error('Failed to load webhook health:', error);
    }
  };

  const registerMissingWebhooks = async () => {
    setRegisteringWebhooks(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE_URL}/shopify/webhooks/register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = r.data?.results || r.data || {};
      const ok = (res.success || []).length;
      const skipped = (res.skipped || []).length;
      const failed = (res.failed || []).length;
      toast.success(`Webhooks: ${ok} registered, ${skipped} already existed${failed ? `, ${failed} failed` : ''}. New Shopify products will now auto-sync.`);
      loadWebhookHealth();
      loadWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register webhooks');
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  // Load sync settings for a store
  const loadSyncSettings = async (storeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/shopify/stores/${storeId}/sync-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSyncSettings(prev => ({
        ...prev,
        [storeId]: response.data.sync_settings
      }));
    } catch (error) {
      console.error('Failed to load sync settings:', error);
      // Set default settings
      setSyncSettings(prev => ({
        ...prev,
        [storeId]: {
          orders_sync_enabled: false,
          orders_sync_since_date: null,
          inventory_sync_enabled: false,
          customers_sync_enabled: false,
          products_sync_enabled: false
        }
      }));
    }
  };

  // Update sync settings for a store
  const updateSyncSettings = async (storeId, newSettings) => {
    setSavingSyncSettings(prev => ({ ...prev, [storeId]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/shopify/stores/${storeId}/sync-settings`,
        newSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSyncSettings(prev => ({
        ...prev,
        [storeId]: newSettings
      }));
      toast.success('Sync settings updated');
    } catch (error) {
      console.error('Failed to update sync settings:', error);
      toast.error('Failed to update sync settings');
    } finally {
      setSavingSyncSettings(prev => ({ ...prev, [storeId]: false }));
    }
  };

  // Toggle individual sync setting
  const handleToggleSyncSetting = async (storeId, settingKey, value) => {
    const currentSettings = syncSettings[storeId] || {
      orders_sync_enabled: false,
      orders_sync_since_date: null,
      inventory_sync_enabled: false,
      customers_sync_enabled: false,
      products_sync_enabled: false
    };
    
    const newSettings = {
      ...currentSettings,
      [settingKey]: value
    };
    
    await updateSyncSettings(storeId, newSettings);
  };

  // Load sync settings when stores are loaded
  useEffect(() => {
    if (stores.length > 0) {
      stores.forEach(store => {
        loadSyncSettings(store.id);
      });
    }
  }, [stores]);

  const [webhookErrorDetails, setWebhookErrorDetails] = useState(null); // {topic, error}[]
  const [reRegistering, setReRegistering] = useState(false);

  // ---- Update Client Secret (recover from a wrong / stale secret
  // stored on the store row, without disconnect/reconnect). Companion
  // to the Re-register button.
  const [updateSecretOpen, setUpdateSecretOpen] = useState(false);
  const [newClientSecret, setNewClientSecret] = useState('');
  const [savingClientSecret, setSavingClientSecret] = useState(false);

  const handleUpdateClientSecret = async () => {
    const trimmed = (newClientSecret || '').trim();
    if (!trimmed) {
      toast.error('Paste the Client Secret from your Shopify Partner Dashboard.');
      return;
    }
    setSavingClientSecret(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API_BASE_URL}/shopify/credentials/update-client-secret`,
        { client_secret: trimmed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const last4 = r.data?.secret_last4 || '????';
      toast.success(
        `✓ Saved Client Secret (…${last4}). Now click "Re-register (Fix Signature Errors)" so existing webhooks rebind to it.`,
        { duration: 9000 },
      );
      setUpdateSecretOpen(false);
      setNewClientSecret('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update Client Secret.');
    } finally {
      setSavingClientSecret(false);
    }
  };

  // Force-rebind every webhook to the CURRENT credentials. Used to
  // recover from "Signature verification failed" — Shopify keeps
  // signing with the secret that was active when each webhook was
  // FIRST registered, so a credential rotation strands them. This
  // deletes & recreates every subscription pointing at us. Safe to
  // re-run. Server-side endpoint: POST /shopify/webhooks/re-register.
  const handleReRegisterWebhooks = async () => {
    if (!window.confirm(
      'Re-register all Shopify webhooks?\n\n'
      + 'This deletes every webhook subscription currently bound to '
      + 'Opswarex on the Shopify side and re-creates them with your '
      + 'CURRENT app credentials. Use this to fix "Signature '
      + 'verification failed" audit-log errors caused by credential '
      + 'rotation. No store data is affected. Webhooks from other apps '
      + 'on this store are untouched.\n\nContinue?'
    )) return;
    setReRegistering(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.post(`${API_BASE_URL}/shopify/webhooks/re-register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const res = r.data?.results || {};
      const recreated = res.recreated?.length || 0;
      const deleted = res.deleted || 0;
      const failed = res.failed || [];
      if (failed.length) {
        toast.error(
          `${recreated} re-created, ${deleted} deleted, ${failed.length} failed. `
          + `First failure: ${failed[0]?.error || failed[0]?.status || 'see logs'}`,
          { duration: 9000 }
        );
        setWebhookErrorDetails(failed.map(f => ({ topic: f.topic, error: f.error || `${f.action}: ${f.status}` })));
      } else {
        toast.success(`✓ Rebound ${recreated} webhook(s) to current credentials. Past signature failures should stop within ~5 minutes.`);
      }
      loadWebhooks();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to re-register webhooks');
    } finally {
      setReRegistering(false);
    }
  };

  const handleRegisterAllWebhooks = async () => {
    setRegisteringWebhooks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/shopify/webhooks/register`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const results = response.data.results;
      const successCount = results.success?.length || 0;
      const existingCount = results.already_exists?.length || 0;
      const failedCount = results.failed?.length || 0;
      const failedRows = results.failed || [];
      
      if (successCount > 0) {
        toast.success(`Registered ${successCount} webhook(s) successfully`);
      }
      if (existingCount > 0) {
        toast.info(`${existingCount} webhook(s) already registered`);
      }
      if (failedCount > 0) {
        // Group identical errors so the toast gives a useful at-a-glance signal
        // (e.g. "scopes required: read_checkouts (3 topics)") and stash the
        // full per-topic breakdown in the modal for deep debugging.
        const reasons = {};
        failedRows.forEach((f) => {
          const key = JSON.stringify(f.error);
          reasons[key] = (reasons[key] || 0) + 1;
        });
        const topReason = Object.keys(reasons).sort((a, b) => reasons[b] - reasons[a])[0];
        const topReasonText = (() => {
          try { return JSON.parse(topReason); } catch { return topReason; }
        })();
        toast.error(
          `${failedCount} webhook(s) failed. Top reason: ${typeof topReasonText === 'string' ? topReasonText : JSON.stringify(topReasonText)}`,
          { duration: 8000 }
        );
        setWebhookErrorDetails(failedRows);
      }
      
      loadWebhooks();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || 'Failed to register webhooks';
      toast.error(msg);
      // 409 SHOPIFY_TOKEN_INVALID also goes here — surface it in the modal too
      if (typeof detail === 'object' && detail?.error) {
        setWebhookErrorDetails([{ topic: 'authentication', error: detail }]);
      }
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  const handleRegisterSelectedWebhooks = async () => {
    if (selectedTopics.length === 0) {
      toast.error('Please select at least one webhook topic');
      return;
    }
    
    setRegisteringWebhooks(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/shopify/webhooks/register-selected`, 
        { topics: selectedTopics },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const results = response.data.results;
      const successCount = results.success?.length || 0;
      const failedCount = results.failed?.length || 0;
      
      if (successCount > 0) {
        toast.success(`Registered ${successCount} webhook(s) successfully`);
      }
      if (failedCount > 0) {
        toast.error(`${failedCount} webhook(s) failed — see details`, { duration: 8000 });
        setWebhookErrorDetails(results.failed);
      }
      
      setWebhookDialogOpen(false);
      setSelectedTopics([]);
      loadWebhooks();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = (typeof detail === 'object' ? detail?.message : detail) || 'Failed to register webhooks';
      toast.error(msg);
      if (typeof detail === 'object' && detail?.error) {
        setWebhookErrorDetails([{ topic: 'authentication', error: detail }]);
      }
    } finally {
      setRegisteringWebhooks(false);
    }
  };

  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/shopify/webhooks/${webhookId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Webhook deleted successfully');
      loadWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete webhook');
    }
  };

  const toggleTopicSelection = (topic) => {
    if (selectedTopics.includes(topic)) {
      setSelectedTopics(selectedTopics.filter(t => t !== topic));
    } else {
      setSelectedTopics([...selectedTopics, topic]);
    }
  };

  const getWebhookTopicInfo = (topic) => {
    return WEBHOOK_TOPICS.find(t => t.topic === topic) || { label: topic, description: '', category: 'Other', icon: Bell };
  };

  const isWebhookRegistered = (topic) => {
    return webhooks.some(w => w.topic === topic);
  };

  const handleConnectStore = async () => {
    if (!shopDomain) {
      toast.error('Please enter your Shopify store domain');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Build query params - include custom credentials if provided
      let url = `${API_BASE_URL}/shopify/auth/install?shop=${shopDomain}`;
      if (customApiKey && customClientSecret) {
        url += `&api_key=${encodeURIComponent(customApiKey)}&client_secret=${encodeURIComponent(customClientSecret)}`;
      }
      
      const response = await axios.get(url, { 
        headers: { Authorization: `Bearer ${token}` } 
      });

      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Shopify connection');
    }
  };

  // Reset form when dialog closes
  const handleConnectDialogChange = (open) => {
    setConnectDialogOpen(open);
    if (!open) {
      setShopDomain('');
      setCustomApiKey('');
      setCustomClientSecret('');
      setShowCustomCredentials(false);
    }
  };

  const handleDisconnect = async (storeId) => {
    if (!window.confirm('Are you sure you want to disconnect this store?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/shopify/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Store disconnected successfully');
      loadStores();
    } catch (error) {
      toast.error('Failed to disconnect store');
    }
  };

  const handleSync = async (storeId, syncType, sinceDate = null) => {
    setSyncing({ ...syncing, [`${storeId}-${syncType}`]: true });

    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/shopify/sync/${storeId}/${syncType}`;
      if (syncType === 'orders' && sinceDate) {
        url += `?since_date=${sinceDate}`;
      }
      
      const response = await axios.post(
        url,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Products, inventory-levels and orders now run in background — poll
      // sync history for progress. Customers already returned 'started' too.
      const isBackground = response.data?.status === 'running';
      if (isBackground) {
        toast.success(response.data.message || `${syncType} sync started in background. Check sync history for progress.`);
      } else {
        toast.success(response.data.message);
      }
      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Sync errors:', response.data.errors);
      }
      loadStores();
      loadSyncHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to sync ${syncType}`);
    } finally {
      setSyncing({ ...syncing, [`${storeId}-${syncType}`]: false });
    }
  };

  const openOrderSyncDialog = (storeId) => {
    setSelectedStoreId(storeId);
    // Default to 30 days ago
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    setSyncSinceDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setOrderSyncDialogOpen(true);
  };

  const handleOrderSync = () => {
    setOrderSyncDialogOpen(false);
    handleSync(selectedStoreId, 'orders', syncSinceDate);
  };

  // Cleanup deleted products states
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  const handleCleanupDryRun = async () => {
    setCleanupLoading(true);
    setCleanupResult(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/shopify/products/cleanup-deleted?dry_run=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCleanupResult(response.data);
      if (response.data.deleted_products?.length === 0) {
        toast.success('All products are in sync - no cleanup needed!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to check deleted products');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupExecute = async () => {
    if (!cleanupResult?.deleted_products?.length) {
      toast.info('No products to clean up');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to DELETE ${cleanupResult.deleted_products.length} products from the database? This action cannot be undone.`)) {
      return;
    }

    setCleanupLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/shopify/products/cleanup-deleted?dry_run=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setCleanupResult(null);
      setCleanupDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cleanup products');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleBulkResync = async (storeId) => {
    if (!window.confirm('This will re-sync ALL existing orders from Shopify with the latest data (payment status, tags, notes, order names). This may take a while. Continue?')) {
      return;
    }

    setSyncing({ ...syncing, [`${storeId}-bulk-resync`]: true });

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/shopify/stores/${storeId}/bulk-resync-orders`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('Some orders had errors:', response.data.errors);
        toast.warning(`${response.data.errors.length} orders had sync errors`);
      }
      loadStores();
      loadSyncHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk re-sync orders');
    } finally {
      setSyncing({ ...syncing, [`${storeId}-bulk-resync`]: false });
    }
  };

  const getSyncStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-400" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-400" />;
      case 'in_progress':
        return <Clock className="h-5 w-5 text-yellow-400 animate-pulse" />;
      default:
        return <AlertCircle className="h-5 w-5 text-wt-text-muted" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wt-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Super Admin Company Selector */}
      <SuperAdminCompanySelector
        selectedCompanyId={selectedCompanyId}
        onCompanyChange={setSelectedCompanyId}
        integrationName="Shopify"
      />
      
      {/* Show message if super admin hasn't selected a company */}
      {needsCompanySelector && !selectedCompanyId ? (
        <div className="text-center py-12 text-wt-text-muted">
          <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>Please select a company above to configure Shopify integration.</p>
        </div>
      ) : (
      <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="font-playfair text-2xl sm:text-3xl md:text-4xl font-bold text-wt-text-primary mb-2">
            Shopify Integration
          </h1>
          <p className="text-wt-text-secondary text-sm">
            Connect and sync your Shopify stores
          </p>
        </div>

        <Dialog open={connectDialogOpen} onOpenChange={handleConnectDialogChange}>
          <DialogTrigger asChild>
            <Button
              className="bg-wt-gold text-black hover:bg-wt-gold-hover font-semibold"
              data-testid="connect-shopify-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Connect Store
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-wt-paper border-wt-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
                Connect Shopify Store
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="shop_domain" className="text-wt-text-primary">
                  Store Domain *
                </Label>
                <Input
                  id="shop_domain"
                  placeholder="yourstore.myshopify.com"
                  value={shopDomain}
                  onChange={(e) => setShopDomain(e.target.value)}
                  className="bg-wt-bg border-wt-border text-wt-text-primary mt-2"
                  data-testid="shop-domain-input"
                />
                <p className="text-xs text-wt-text-muted mt-1">
                  Enter your Shopify store domain (e.g., yourstore.myshopify.com)
                </p>
              </div>

              {/* Custom API Credentials Section */}
              <div className="border border-wt-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <Label className="text-wt-text-primary text-sm font-medium">
                      Custom API Credentials
                    </Label>
                    <p className="text-xs text-wt-text-muted">
                      For companies with their own Shopify app
                    </p>
                  </div>
                  <Switch
                    checked={showCustomCredentials}
                    onCheckedChange={setShowCustomCredentials}
                    data-testid="custom-credentials-toggle"
                  />
                </div>
                
                {showCustomCredentials && (
                  <div className="space-y-3 pt-3 border-t border-wt-border">
                    <div>
                      <Label htmlFor="api_key" className="text-wt-text-secondary text-sm">
                        API Key
                      </Label>
                      <Input
                        id="api_key"
                        placeholder="Your Shopify App API Key"
                        value={customApiKey}
                        onChange={(e) => setCustomApiKey(e.target.value)}
                        className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                        data-testid="shopify-api-key-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_secret" className="text-wt-text-secondary text-sm">
                        Client Secret
                      </Label>
                      <Input
                        id="client_secret"
                        type="password"
                        placeholder="Your Shopify App Client Secret"
                        value={customClientSecret}
                        onChange={(e) => setCustomClientSecret(e.target.value)}
                        className="bg-wt-bg border-wt-border text-wt-text-primary mt-1"
                        data-testid="shopify-client-secret-input"
                      />
                    </div>
                    <p className="text-xs text-amber-400">
                      Get these from your Shopify Partner Dashboard → Apps → Your App → API credentials
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <p className="text-xs text-blue-400">
                  {showCustomCredentials 
                    ? "Using your custom Shopify app credentials. You'll be redirected to Shopify to authorize the connection."
                    : "You'll be redirected to Shopify to authorize the connection using our default app credentials."
                  }
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => handleConnectDialogChange(false)}
                  className="text-wt-text-secondary"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConnectStore}
                  disabled={showCustomCredentials && (!customApiKey || !customClientSecret)}
                  className="bg-wt-gold text-black hover:bg-wt-gold-hover"
                  data-testid="submit-connect-button"
                >
                  Connect Store
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {stores.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-12 text-center"
        >
          <ShoppingBag className="h-16 w-16 mx-auto mb-4 text-wt-text-muted opacity-30" />
          <h3 className="text-xl font-semibold text-wt-text-primary mb-2">
            No Shopify Stores Connected
          </h3>
          <p className="text-wt-text-secondary mb-6">
            Connect your Shopify store to sync products, orders, and customers
          </p>
          <Button
            onClick={() => setConnectDialogOpen(true)}
            className="bg-wt-gold text-black hover:bg-wt-gold-hover"
          >
            <Plus className="h-4 w-4 mr-2" />
            Connect Your First Store
          </Button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Tenant-wide multi-origin routing toggle. Hidden until at
              least one store is connected — single-tenant UX is
              cleaner that way. */}
          <div
            className="bg-wt-paper border border-wt-border rounded-xl p-4 flex items-start gap-3"
            data-testid="multi-origin-routing-card"
          >
            <Truck className="h-5 w-5 text-wt-gold mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-wt-text-primary">
                  Multi-Origin Web Order Routing
                </h3>
                <Switch
                  checked={multiOriginEnabled}
                  onCheckedChange={toggleMultiOrigin}
                  disabled={savingMultiOrigin}
                  data-testid="multi-origin-routing-toggle"
                />
              </div>
              <p className="text-xs text-wt-text-muted mt-1">
                For Shopify web orders that ship from multiple
                warehouses, fetch the FulfillmentOrder API to learn
                each line item's assigned location. Adds ~200 ms per
                order webhook. Leave OFF for single-warehouse stores
                or when all your fulfillment is from POS / app
                channels (those already carry origin info).
              </p>
            </div>
          </div>

          {/* SKU audit: Shopify variant SKUs vs Opswarex inventory SKUs */}
          <div className="bg-wt-paper border border-wt-border rounded-xl p-4" data-testid="sku-audit-card">
            <div className="flex items-start gap-3">
              <Barcode className="h-5 w-5 text-wt-gold mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="text-sm font-semibold text-wt-text-primary">Shopify SKU Audit</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => runSkuAudit(false)} disabled={skuAuditLoading}
                      className="border-wt-border text-wt-text-primary" data-testid="sku-audit-scan-btn">
                      {skuAuditLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
                      Scan SKUs
                    </Button>
                    {skuAudit && skuAudit.mismatches.length > 0 && (
                      <Button size="sm" onClick={() => runSkuAudit(true)} disabled={skuAuditLoading}
                        className="bg-wt-gold text-black hover:bg-wt-gold/90" data-testid="sku-audit-fix-btn">
                        Fix {skuAudit.mismatches.length} on Shopify
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-wt-text-muted mt-1">
                  Finds Shopify products whose variant SKU differs from your Opswarex inventory SKU
                  (e.g. Danube asset codes like 179900152287 instead of AFR9918) and corrects them on
                  Shopify so pushed orders show the right SKU.
                </p>
                {skuAudit && (
                  <div className="mt-3 text-xs" data-testid="sku-audit-results">
                    <p className="text-wt-text-secondary">
                      Checked {skuAudit.checked} linked variants — {skuAudit.mismatches.length} mismatch{skuAudit.mismatches.length === 1 ? '' : 'es'}
                      {skuAudit.applied ? ` · ${skuAudit.updated} fixed` : ''}
                      {skuAudit.errors?.length ? ` · ${skuAudit.errors.length} failed` : ''}
                    </p>
                    {skuAudit.mismatches.length > 0 && (
                      <div className="mt-2 max-h-48 overflow-y-auto rounded border border-wt-border">
                        <table className="w-full">
                          <thead><tr className="bg-wt-subtle text-wt-text-muted text-left">
                            <th className="px-2 py-1.5">Product</th>
                            <th className="px-2 py-1.5">Shopify SKU</th>
                            <th className="px-2 py-1.5">Correct SKU</th>
                          </tr></thead>
                          <tbody>
                            {skuAudit.mismatches.map((m) => (
                              <tr key={m.variant_id} className="border-t border-wt-border">
                                <td className="px-2 py-1.5 text-wt-text-primary truncate max-w-[220px]">{m.title}</td>
                                <td className="px-2 py-1.5 text-red-400">{m.shopify_sku || '—'}</td>
                                <td className="px-2 py-1.5 text-emerald-400">{m.correct_sku}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {stores.map((store, index) => (
            <motion.div
              key={store.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
              data-testid={`store-${store.id}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                    <ShoppingBag className="h-8 w-8 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
                      {store.shop_domain}
                      <a
                        href={`https://${store.shop_domain}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-wt-gold hover:text-wt-gold-hover"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </h3>
                    <p className="text-sm text-wt-text-secondary">
                      Connected: {formatDateTime(store.installed_at)}
                    </p>
                    {store.last_sync && (
                      <p className="text-xs text-wt-text-muted">
                        Last manual product sync: {formatDateTime(store.last_sync)}
                      </p>
                    )}
                    {webhookHealth[store.id] && (() => {
                      const h = webhookHealth[store.id];
                      const ago = (iso) => {
                        if (!iso) return 'never';
                        const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
                        if (m < 60) return `${m}m ago`;
                        if (m < 2880) return `${Math.round(m / 60)}h ago`;
                        return `${Math.round(m / 1440)}d ago`;
                      };
                      return (
                        <div className="text-xs mt-0.5 space-y-0.5" data-testid={`webhook-health-${store.id}`}>
                          <p>
                            {h.status === 'healthy' && (
                              <span className="text-emerald-400">● Auto sync active — last event {ago(h.last_webhook_at)} ({h.last_webhook_topic}) · {h.events_24h} events in 24h{h.failed_7d > 0 ? ` · ${h.failed_7d} failed in 7d` : ''}</span>
                            )}
                            {h.status === 'stale' && (
                              <span className="text-amber-400">● Auto sync STALE — last webhook {ago(h.last_webhook_at)}. Scroll down and click "Re-register (Fix Signature Errors)" to rebind webhooks.</span>
                            )}
                            {h.status === 'failing' && (
                              <span className="text-red-400">● Webhooks arriving but FAILING ({h.failed_7d}/{h.events_7d} failed in 7d{h.last_webhook_error ? ` — ${String(h.last_webhook_error).slice(0, 60)}` : ''}). Use "Re-register (Fix Signature Errors)" below.</span>
                            )}
                            {h.status === 'no_events' && (
                              <span className="text-red-400">● No webhooks received yet — auto sync inactive. Click "Re-register (Fix Signature Errors)" below.</span>
                            )}
                          </p>
                          <p className="text-wt-text-muted">
                            Product events: {h.last_product_event_at ? ago(h.last_product_event_at) : <span className="text-red-400">never received</span>}
                            {' · '}Order events: {h.last_order_event_at ? ago(h.last_order_event_at) : 'never'}
                          </p>
                          {h.missing_topics && h.missing_topics.length > 0 && (
                            <div className="flex items-center gap-2 flex-wrap" data-testid={`missing-webhooks-${store.id}`}>
                              <span className="text-red-400">
                                ⚠ {h.missing_topics.length} webhook subscription{h.missing_topics.length > 1 ? 's' : ''} MISSING on Shopify: {h.missing_topics.join(', ')} — this is why those changes don't auto-sync
                              </span>
                              <Button size="sm" className="h-6 px-2 text-[11px] bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30"
                                disabled={registeringWebhooks} onClick={registerMissingWebhooks}
                                data-testid={`register-missing-btn-${store.id}`}>
                                {registeringWebhooks ? 'Registering…' : 'Register missing webhooks now'}
                              </Button>
                            </div>
                          )}
                          {h.registration_error && (
                            <p className="text-amber-400/80">Could not verify subscriptions on Shopify: {h.registration_error}</p>
                          )}
                        </div>
                      );
                    })()}
                    <div className="mt-2">
                      <ChannelDisplayNameField
                        match={(c) => c.channel_type === 'shopify' && (
                          (c.credentials_ref || {}).store_id === store.id ||
                          c.name === store.shop_domain
                        )}
                      />
                    </div>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(store.id)}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`disconnect-${store.id}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Disconnect
                </Button>
              </div>

              {/* Stale-token banner — shown when the saved access_token has
                  been rejected by Shopify (most often because the app
                  credentials changed since this store was first connected,
                  OR because the legacy non-expiring token has been
                  deprecated by Shopify and our silent token-exchange
                  migration also failed).
                  The merchant must disconnect + reconnect to fix it. */}
              {(store.auth_status === 'invalid' || store.auth_status === 'needs_reconnect') && (
                <div
                  className="mt-3 p-3 rounded-lg border border-red-500/40 bg-red-500/10 text-sm"
                  data-testid={`store-auth-warning-${store.id}`}
                >
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-red-300 font-medium">Reconnect required</p>
                      <p className="text-xs text-wt-text-muted mt-1">
                        {store.auth_error || 'Shopify rejected this store\'s access token. Disconnect and reconnect to issue a fresh expiring access token.'}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 border-red-400/40 text-red-300 hover:bg-red-500/20"
                        onClick={() => handleDisconnect(store.id)}
                        data-testid={`reconnect-cta-${store.id}`}
                      >
                        Disconnect &amp; Reconnect
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Sync Settings Section */}
              <div className="bg-wt-bg rounded-lg p-4 mb-4 border border-wt-border overflow-visible">
                <h4 className="text-sm font-medium text-wt-text-primary mb-4 flex items-center gap-2">
                  <RefreshCw className="h-4 w-4" />
                  Auto Sync Settings
                </h4>
                <p className="text-xs text-wt-text-muted mb-4">
                  Control which data types are automatically synced with Shopify. When disabled, data will not be synced automatically via webhooks.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
                  {/* Orders Sync */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <ShoppingBag className="h-4 w-4 text-purple-400" />
                        <span className="text-sm text-wt-text-primary">Order Sync</span>
                      </div>
                      <Switch
                        checked={syncSettings[store.id]?.orders_sync_enabled || false}
                        onCheckedChange={(checked) => handleToggleSyncSetting(store.id, 'orders_sync_enabled', checked)}
                        disabled={savingSyncSettings[store.id]}
                      />
                    </div>
                    {syncSettings[store.id]?.orders_sync_enabled && (
                      <div className="ml-6 pl-4 border-l-2 border-purple-500/30">
                        <Label className="text-xs text-wt-text-muted">Sync orders from date:</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal bg-wt-paper border-wt-border text-wt-text-primary h-9 mt-1",
                                !syncSettings[store.id]?.orders_sync_since_date && "text-wt-text-muted"
                              )}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {syncSettings[store.id]?.orders_sync_since_date ? (
                                format(parseISO(syncSettings[store.id].orders_sync_since_date), "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-wt-surface border-wt-border" align="start">
                            <Calendar
                              mode="single"
                              selected={syncSettings[store.id]?.orders_sync_since_date ? parseISO(syncSettings[store.id].orders_sync_since_date) : undefined}
                              onSelect={(date) => {
                                const newSettings = {
                                  ...syncSettings[store.id],
                                  orders_sync_since_date: date ? format(date, 'yyyy-MM-dd') : null
                                };
                                updateSyncSettings(store.id, newSettings);
                              }}
                              initialFocus
                              className="bg-wt-surface text-wt-text-primary"
                            />
                          </PopoverContent>
                        </Popover>
                        <p className="text-[10px] text-wt-text-muted mt-1">
                          Leave empty to sync all future orders
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Inventory Sync */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-teal-400" />
                      <span className="text-sm text-wt-text-primary">Inventory Sync</span>
                    </div>
                    <Switch
                      checked={syncSettings[store.id]?.inventory_sync_enabled || false}
                      onCheckedChange={(checked) => handleToggleSyncSetting(store.id, 'inventory_sync_enabled', checked)}
                      disabled={savingSyncSettings[store.id]}
                    />
                  </div>

                  {/* Customers Sync */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-orange-400" />
                      <span className="text-sm text-wt-text-primary">Customers Sync</span>
                    </div>
                    <Switch
                      checked={syncSettings[store.id]?.customers_sync_enabled || false}
                      onCheckedChange={(checked) => handleToggleSyncSetting(store.id, 'customers_sync_enabled', checked)}
                      disabled={savingSyncSettings[store.id]}
                    />
                  </div>

                  {/* Products Sync */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-blue-400" />
                      <span className="text-sm text-wt-text-primary">Products Sync</span>
                    </div>
                    <Switch
                      checked={syncSettings[store.id]?.products_sync_enabled || false}
                      onCheckedChange={(checked) => handleToggleSyncSetting(store.id, 'products_sync_enabled', checked)}
                      disabled={savingSyncSettings[store.id]}
                    />
                  </div>
                </div>
                
                {savingSyncSettings[store.id] && (
                  <p className="text-xs text-wt-gold mt-2 flex items-center gap-1">
                    <RefreshCw className="h-3 w-3 animate-spin" />
                    Saving...
                  </p>
                )}
              </div>

              {/* Manual Sync Section */}
              <h4 className="text-sm font-medium text-wt-text-primary mb-3 flex items-center gap-2">
                <RefreshCw className="h-4 w-4" />
                Manual Sync
              </h4>
              <p className="text-xs text-wt-text-muted mb-4">
                Manually trigger one-time sync operations regardless of auto sync settings.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button
                  onClick={() => handleSync(store.id, 'products')}
                  disabled={syncing[`${store.id}-products`]}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid={`sync-products-${store.id}`}
                >
                  {syncing[`${store.id}-products`] ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sync Products
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => handleSync(store.id, 'inventory-levels')}
                  disabled={syncing[`${store.id}-inventory-levels`]}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid={`sync-inventory-levels-${store.id}`}
                >
                  {syncing[`${store.id}-inventory-levels`] ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sync Stock Levels
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => openOrderSyncDialog(store.id)}
                  disabled={syncing[`${store.id}-orders`]}
                  className="bg-purple-500 hover:bg-purple-600 text-white"
                  data-testid={`sync-orders-${store.id}`}
                >
                  {syncing[`${store.id}-orders`] ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      Sync Orders
                    </>
                  )}
                </Button>

                <Button
                  onClick={() => handleSync(store.id, 'customers')}
                  disabled={syncing[`${store.id}-customers`]}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  data-testid={`sync-customers-${store.id}`}
                >
                  {syncing[`${store.id}-customers`] ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Syncing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Sync Customers
                    </>
                  )}
                </Button>
              </div>

              {/* Bulk Re-Sync Section */}
              <div className="mt-4 pt-4 border-t border-wt-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-wt-text-primary">Bulk Re-Sync Orders</h4>
                    <p className="text-xs text-wt-text-muted">
                      Update all existing orders with latest data from Shopify (payment status, tags, notes, order names)
                    </p>
                  </div>
                  <Button
                    onClick={() => handleBulkResync(store.id)}
                    disabled={syncing[`${store.id}-bulk-resync`]}
                    variant="outline"
                    className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                    data-testid={`bulk-resync-${store.id}`}
                  >
                    {syncing[`${store.id}-bulk-resync`] ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Re-syncing All...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Bulk Re-Sync
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Cleanup Deleted Products Section */}
              <div className="mt-4 pt-4 border-t border-wt-border">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-wt-text-primary">Cleanup Deleted Products</h4>
                    <p className="text-xs text-wt-text-muted">
                      Find and remove products from local database that were deleted from Shopify
                    </p>
                  </div>
                  <Button
                    onClick={() => setCleanupDialogOpen(true)}
                    variant="outline"
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    data-testid="cleanup-deleted-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Cleanup Deleted
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Webhooks Management Section */}
      {stores.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-semibold text-wt-text-primary flex items-center gap-2">
                <Webhook className="h-6 w-6 text-wt-gold" />
                Webhooks
              </h2>
              <p className="text-sm text-wt-text-secondary">
                Real-time notifications from Shopify for automatic updates
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={loadWebhooks}
                variant="outline"
                className="border-wt-border text-wt-text-secondary hover:bg-wt-subtle"
                disabled={webhooksLoading}
                data-testid="refresh-webhooks-button"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${webhooksLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Dialog open={webhookDialogOpen} onOpenChange={setWebhookDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-wt-gold text-wt-gold hover:bg-wt-gold/10"
                    data-testid="add-webhook-button"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Webhook
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
                      Add Webhooks
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-wt-text-secondary">
                      Select the webhook topics you want to register. Already registered webhooks are marked with a green check.
                    </p>
                    
                    {/* Group by category */}
                    {['Orders', 'Checkouts', 'Products', 'Customers', 'Inventory'].map(category => (
                      <div key={category} className="space-y-2">
                        <h4 className="font-semibold text-wt-text-primary border-b border-wt-border pb-1">{category}</h4>
                        <div className="grid grid-cols-1 gap-2">
                          {WEBHOOK_TOPICS.filter(t => t.category === category).map(topic => {
                            const isRegistered = isWebhookRegistered(topic.topic);
                            const isSelected = selectedTopics.includes(topic.topic);
                            const IconComponent = topic.icon;
                            
                            return (
                              <div
                                key={topic.topic}
                                onClick={() => !isRegistered && toggleTopicSelection(topic.topic)}
                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                  isRegistered 
                                    ? 'bg-green-500/10 border-green-500/30 cursor-default' 
                                    : isSelected 
                                      ? 'bg-wt-gold/10 border-wt-gold' 
                                      : 'bg-wt-subtle border-wt-border hover:border-wt-gold/50'
                                }`}
                                data-testid={`webhook-topic-${topic.topic.replace('/', '-')}`}
                              >
                                <div className="flex items-center gap-3">
                                  <IconComponent className={`h-4 w-4 ${isRegistered ? 'text-green-400' : isSelected ? 'text-wt-gold' : 'text-wt-text-muted'}`} />
                                  <div>
                                    <p className={`text-sm font-medium ${isRegistered ? 'text-green-400' : 'text-wt-text-primary'}`}>
                                      {topic.label}
                                    </p>
                                    <p className="text-xs text-wt-text-muted">{topic.description}</p>
                                  </div>
                                </div>
                                {isRegistered ? (
                                  <CheckCircle className="h-5 w-5 text-green-400" />
                                ) : (
                                  <div className={`h-5 w-5 rounded border-2 ${isSelected ? 'bg-wt-gold border-wt-gold' : 'border-wt-border'}`}>
                                    {isSelected && <CheckCircle className="h-4 w-4 text-black" />}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                    
                    <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setWebhookDialogOpen(false);
                          setSelectedTopics([]);
                        }}
                        className="text-wt-text-secondary"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleRegisterSelectedWebhooks}
                        disabled={selectedTopics.length === 0 || registeringWebhooks}
                        className="bg-wt-gold text-black hover:bg-wt-gold-hover"
                        data-testid="register-selected-webhooks-button"
                      >
                        {registeringWebhooks ? (
                          <>
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                            Registering...
                          </>
                        ) : (
                          <>
                            <Plus className="h-4 w-4 mr-2" />
                            Register Selected ({selectedTopics.length})
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <Button
                onClick={handleRegisterAllWebhooks}
                disabled={registeringWebhooks}
                className="bg-wt-gold text-black hover:bg-wt-gold-hover"
                data-testid="register-all-webhooks-button"
              >
                {registeringWebhooks ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Registering...
                  </>
                ) : (
                  <>
                    <Radio className="h-4 w-4 mr-2" />
                    Register All
                  </>
                )}
              </Button>
              <Button
                onClick={handleReRegisterWebhooks}
                disabled={reRegistering || registeringWebhooks}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                title="Fix 'Signature verification failed' errors by rebinding webhooks to the current credentials"
                data-testid="re-register-webhooks-button"
              >
                {reRegistering ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Re-binding...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Re-register (Fix Signature Errors)
                  </>
                )}
              </Button>
              {/* Update Client Secret — recovery action for the case
                  where the SAVED secret on the store row is wrong
                  (wrong copy-paste, hidden whitespace, partner
                  dashboard rotation). Re-register alone can't fix
                  this — the new webhooks would still be signed by
                  Shopify with the actual secret and fail verification
                  against our stale stored value. */}
              <Button
                onClick={() => setUpdateSecretOpen(true)}
                variant="outline"
                className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
                title="Paste your current Shopify app Client Secret to fix persistent signature failures"
                data-testid="update-client-secret-button"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                Update Client Secret
              </Button>
            </div>
          </div>

          {/* Registered Webhooks List */}
          <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
            {webhooksLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 text-wt-gold animate-spin" />
              </div>
            ) : webhooks.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="h-12 w-12 mx-auto mb-4 text-wt-text-muted opacity-30" />
                <h3 className="text-lg font-semibold text-wt-text-primary mb-2">
                  No Webhooks Registered
                </h3>
                <p className="text-sm text-wt-text-secondary mb-4">
                  Register webhooks to receive real-time updates from Shopify
                </p>
                <Button
                  onClick={handleRegisterAllWebhooks}
                  className="bg-wt-gold text-black hover:bg-wt-gold-hover"
                >
                  <Radio className="h-4 w-4 mr-2" />
                  Register All Recommended Webhooks
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-wt-border">
                {/* Header */}
                <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-wt-subtle text-xs font-semibold text-wt-text-primary uppercase">
                  <div className="col-span-4">Topic</div>
                  <div className="col-span-5">Webhook URL</div>
                  <div className="col-span-2">Created</div>
                  <div className="col-span-1">Actions</div>
                </div>
                
                {/* Webhook rows */}
                {webhooks.map((webhook) => {
                  const topicInfo = getWebhookTopicInfo(webhook.topic);
                  const IconComponent = topicInfo.icon;
                  
                  return (
                    <div
                      key={webhook.id}
                      className="grid grid-cols-12 gap-4 px-6 py-4 items-center hover:bg-wt-subtle"
                      data-testid={`webhook-row-${webhook.id}`}
                    >
                      <div className="col-span-4 flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/10">
                          <IconComponent className="h-4 w-4 text-green-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-wt-text-primary">{topicInfo.label}</p>
                          <p className="text-xs text-wt-text-muted">{webhook.topic}</p>
                        </div>
                      </div>
                      <div className="col-span-5">
                        <p className="text-xs text-wt-text-secondary truncate" title={webhook.address}>
                          {webhook.address}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-wt-text-muted">
                          {webhook.created_at ? formatDateTime(webhook.created_at) : '-'}
                        </p>
                      </div>
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteWebhook(webhook.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          data-testid={`delete-webhook-${webhook.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-blue-400">How Webhooks Work</h4>
                <p className="text-xs text-blue-300 mt-1">
                  Webhooks allow Shopify to send real-time notifications to your app when events occur (e.g., order paid, product updated). 
                  Click "Register All" to set up all recommended webhooks automatically, or use "Add Webhook" to select specific ones.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {syncHistory.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-wt-text-primary">Sync History</h2>
          <div className="bg-wt-paper border border-wt-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-wt-subtle border-b border-wt-border">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase">
                    Items Synced
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase">
                    Started
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-wt-text-primary uppercase">
                    Duration
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-wt-border">
                {syncHistory.slice(0, 10).map((sync) => (
                  <tr key={sync.id} className="hover:bg-wt-subtle">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {getSyncStatusIcon(sync.status)}
                        <span className="text-sm text-wt-text-primary capitalize">
                          {sync.status.replace('_', ' ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-wt-text-primary capitalize">
                      {sync.sync_type}
                    </td>
                    <td className="px-6 py-4 text-sm text-wt-text-primary font-semibold">
                      {sync.items_synced}
                    </td>
                    <td className="px-6 py-4 text-sm text-wt-text-secondary">
                      {formatDateTime(sync.started_at)}
                    </td>
                    <td className="px-6 py-4 text-sm text-wt-text-muted">
                      {sync.completed_at
                        ? `${Math.round(
                            (new Date(sync.completed_at) - new Date(sync.started_at)) / 1000
                          )}s`
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Order Sync Dialog with Date Picker */}
      <Dialog open={orderSyncDialogOpen} onOpenChange={setOrderSyncDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Sync Orders from Shopify
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="since_date" className="text-wt-text-primary">
                Sync orders from date
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal bg-wt-bg border-wt-border text-wt-text-primary h-10 mt-2",
                      !syncSinceDate && "text-wt-text-muted"
                    )}
                    data-testid="order-sync-date-input"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {syncSinceDate ? (
                      format(parseISO(syncSinceDate), "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-wt-surface border-wt-border" align="start">
                  <Calendar
                    mode="single"
                    selected={syncSinceDate ? parseISO(syncSinceDate) : undefined}
                    onSelect={(date) => setSyncSinceDate(date ? format(date, 'yyyy-MM-dd') : '')}
                    initialFocus
                    className="bg-wt-surface text-wt-text-primary"
                  />
                </PopoverContent>
              </Popover>
              <p className="text-xs text-wt-text-muted mt-1">
                Only orders created on or after this date will be synced
              </p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
              <p className="text-xs text-blue-400">
                Orders will be synced with product images. Existing orders (already synced) will be updated with latest data.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="ghost"
                onClick={() => setOrderSyncDialogOpen(false)}
                className="text-wt-text-secondary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleOrderSync}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid="confirm-order-sync-button"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                Start Sync
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cleanup Deleted Products Dialog */}
      <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl">
              Cleanup Deleted Products
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-wt-text-secondary text-sm">
              This will check all products in your local inventory against Shopify and find any that have been deleted from Shopify.
            </p>

            {!cleanupResult ? (
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                <p className="text-sm text-yellow-400">
                  Click "Check for Deleted" to scan your inventory. No changes will be made until you confirm.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {cleanupResult.deleted_products?.length > 0 ? (
                  <>
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <p className="text-sm text-red-400 font-medium">
                        Found {cleanupResult.deleted_products.length} products deleted from Shopify
                      </p>
                    </div>
                    <div className="max-h-60 overflow-y-auto border border-wt-border rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-wt-subtle sticky top-0">
                          <tr>
                            <th className="text-left p-2 text-wt-text-muted">SKU</th>
                            <th className="text-left p-2 text-wt-text-muted">Product Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cleanupResult.deleted_products.map((product, idx) => (
                            <tr key={idx} className="border-t border-wt-border/50">
                              <td className="p-2 text-wt-text-primary font-mono text-xs">{product.sku || '-'}</td>
                              <td className="p-2 text-wt-text-secondary">{product.name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
                    <p className="text-sm text-green-400">
                      ✓ All products are in sync! No cleanup needed.
                    </p>
                  </div>
                )}
                <p className="text-xs text-wt-text-muted">
                  Checked {cleanupResult.checked} products
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-wt-border">
              <Button
                variant="ghost"
                onClick={() => {
                  setCleanupDialogOpen(false);
                  setCleanupResult(null);
                }}
                className="text-wt-text-secondary"
              >
                Close
              </Button>
              {!cleanupResult ? (
                <Button
                  onClick={handleCleanupDryRun}
                  disabled={cleanupLoading}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  data-testid="check-deleted-btn"
                >
                  {cleanupLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Check for Deleted
                    </>
                  )}
                </Button>
              ) : cleanupResult.deleted_products?.length > 0 && (
                <Button
                  onClick={handleCleanupExecute}
                  disabled={cleanupLoading}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  data-testid="execute-cleanup-btn"
                >
                  {cleanupLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Cleaning...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete from Database
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Webhook Error Details Dialog — surfaces the actual per-topic
          Shopify error returned by the backend so merchants (and us) can
          see WHY registration failed (most common: missing scopes, invalid
          access token, app URL not whitelisted on the Partner Dashboard). */}
      <Dialog open={!!webhookErrorDetails} onOpenChange={(o) => !o && setWebhookErrorDetails(null)}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="webhook-error-details-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary font-playfair text-2xl flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-amber-400" />
              Webhook registration errors
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-wt-text-muted">
              Each row below is the exact response Shopify sent back. Common causes:
            </p>
            <ul className="text-xs text-wt-text-muted list-disc pl-5 space-y-1">
              <li><span className="text-wt-text-primary">"Non-expiring access tokens are no longer accepted"</span> — Shopify deprecated legacy long-lived tokens (Apr 2026). Disconnect &amp; reconnect to issue a fresh expiring offline token with auto-refresh.</li>
              <li><span className="text-wt-text-primary">"requires merchant approval for ... scope"</span> — disconnect &amp; reconnect the store; the OAuth scopes have changed.</li>
              <li><span className="text-wt-text-primary">"Address is not allowed"</span> — your app's URL isn't whitelisted on the Partner Dashboard.</li>
              <li><span className="text-wt-text-primary">"Invalid API key or access token"</span> — token is stale; reconnect the store.</li>
            </ul>
            <div className="border border-wt-border rounded-lg overflow-hidden mt-3">
              <table className="w-full text-sm">
                <thead className="bg-wt-subtle">
                  <tr>
                    <th className="text-left p-2 text-wt-text-secondary">Topic</th>
                    <th className="text-left p-2 text-wt-text-secondary">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(webhookErrorDetails || []).map((row, idx) => (
                    <tr key={idx} className="border-t border-wt-border">
                      <td className="p-2 text-wt-text-primary font-mono text-xs">{row.topic}</td>
                      <td className="p-2 text-red-300 text-xs">
                        {typeof row.error === 'object'
                          ? JSON.stringify(row.error)
                          : String(row.error)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Client Secret dialog. The merchant pastes the current
          Client Secret from their Shopify Partner Dashboard; backend
          self-validates (HMAC round-trip) and persists onto the store
          row. Companion to the Re-register Webhooks button. */}
      <Dialog open={updateSecretOpen} onOpenChange={setUpdateSecretOpen}>
        <DialogContent className="max-w-xl bg-wt-paper border border-wt-border text-wt-text-primary" data-testid="update-client-secret-dialog">
          <DialogHeader>
            <DialogTitle className="text-wt-text-primary flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-rose-400" />
              Update Shopify Client Secret
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-wt-text-secondary space-y-2">
              <p>
                Use this when "Re-register" alone doesn't stop the
                <span className="text-rose-300"> "Signature verification failed" </span>
                errors. Most often the saved Client Secret on this store row
                doesn't match the actual secret your Shopify custom app is
                using to sign webhooks (wrong paste at install time, hidden
                whitespace, or a Partner Dashboard rotation that wasn't
                mirrored here).
              </p>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Open your Shopify <strong>Partner Dashboard</strong> → Apps → your custom app → <strong>Client credentials</strong>.</li>
                <li>Copy the <strong>Client Secret</strong> (sometimes called "API secret key").</li>
                <li>Paste it below and click Save. Then click <strong>"Re-register (Fix Signature Errors)"</strong> on this page.</li>
              </ol>
            </div>
            <div>
              <Label htmlFor="new-client-secret" className="text-wt-text-secondary text-sm">
                Client Secret
              </Label>
              <Input
                id="new-client-secret"
                type="password"
                value={newClientSecret}
                onChange={(e) => setNewClientSecret(e.target.value)}
                placeholder="shpss_… or a 32-char hex string"
                className="mt-2 bg-wt-bg border-wt-border text-wt-text-primary"
                data-testid="new-client-secret-input"
                autoComplete="off"
                spellCheck={false}
              />
              <p className="text-[11px] text-wt-text-muted mt-1">
                We trim whitespace and zero-width characters automatically. The value is HMAC-validated server-side before save.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={() => { setUpdateSecretOpen(false); setNewClientSecret(''); }}
                disabled={savingClientSecret}
                data-testid="cancel-update-client-secret-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateClientSecret}
                disabled={savingClientSecret || !newClientSecret.trim()}
                className="bg-rose-500 text-white hover:bg-rose-500/90"
                data-testid="save-client-secret-button"
              >
                {savingClientSecret ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Validating & Saving...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    Save Client Secret
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShopifyIntegrationPage;
